import React, { useState, useEffect, useCallback } from 'react';
import API_URL from './config';

// Generate hours for time selection (midnight to midnight)
const HOURS = [];
for (let h = 0; h <= 24; h++) {
  const label = h === 0 ? '12:00 AM (Start of Day)' :
                h === 24 ? '12:00 AM (End of Day)' : 
                h === 12 ? '12:00 PM' :
                h < 12 ? `${h}:00 AM` : `${h - 12}:00 PM`;
  HOURS.push({ value: h, label });
}

function AdminPricing({ token }) {
  const [rules, setRules] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [timezone, setTimezone] = useState('America/New_York');
  const [form, setForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    is_recurring: true,
    start_hour: 0,
    end_hour: 24,
    full_turf_price: '',
    half_turf_price: '',
    cage_price: '',
    priority: 0
  });

  const fetchRules = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/pricing`);
      const data = await res.json();
      setRules(Array.isArray(data) ? data : []);
    } catch {
      setRules([]);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const resetForm = () => {
    setForm({
      name: '',
      start_date: '',
      end_date: '',
      is_recurring: true,
      start_hour: 0,
      end_hour: 24,
      full_turf_price: '',
      half_turf_price: '',
      cage_price: '',
      priority: 0
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    // Validate at least one price is set
    if (!form.full_turf_price && !form.half_turf_price && !form.cage_price) {
      setError('At least one price must be set');
      return;
    }

    try {
      const url = editingId 
        ? `${API_URL}/api/pricing/${editingId}`
        : `${API_URL}/api/pricing`;
      
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...form,
          start_hour: parseInt(form.start_hour),
          end_hour: parseInt(form.end_hour),
          full_turf_price: form.full_turf_price ? parseFloat(form.full_turf_price) : null,
          half_turf_price: form.half_turf_price ? parseFloat(form.half_turf_price) : null,
          cage_price: form.cage_price ? parseFloat(form.cage_price) : null,
          priority: parseInt(form.priority) || 0
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save pricing rule');

      setMessage(editingId ? 'Pricing rule updated!' : 'Pricing rule created!');
      resetForm();
      fetchRules();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (rule) => {
    setForm({
      name: rule.name || '',
      start_date: rule.start_date ? rule.start_date.slice(0, 10) : '',
      end_date: rule.end_date ? rule.end_date.slice(0, 10) : '',
      is_recurring: rule.is_recurring || false,
      start_hour: rule.start_hour || 6,
      end_hour: rule.end_hour || 24,
      full_turf_price: rule.full_turf_price || '',
      half_turf_price: rule.half_turf_price || '',
      cage_price: rule.cage_price || '',
      priority: rule.priority || 0
    });
    setEditingId(rule.id);
    setShowForm(true);
    setMessage('');
    setError('');
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete pricing rule "${rule.name || 'Unnamed Rule'}"?`)) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/pricing/${rule.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete');

      setMessage('Pricing rule deleted');
      fetchRules();
    } catch (err) {
      setError(err.message);
    }
  };

  // Fetch timezone setting
  useEffect(() => {
    fetch(`${API_URL}/api/settings/timezone`)
      .then(r => r.json())
      .then(data => { if (data.value) setTimezone(data.value); })
      .catch(() => {});
  }, []);

  const formatDate = (dateStr, isRecurring = false) => {
    if (!dateStr) return '-';
    const datePart = dateStr.slice(0, 10);
    const [year, month, day] = datePart.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (isRecurring) {
      return date.toLocaleDateString('en-US', { timeZone: timezone, month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString('en-US', { timeZone: timezone, month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDateRange = (start, end, isRecurring = false) => {
    if (!start || !end) return '-';
    const range = `${formatDate(start, isRecurring)} - ${formatDate(end, isRecurring)}`;
    return isRecurring ? `${range} (Yearly)` : range;
  };

  const getHourLabel = (hour) => {
    if (hour === 0 || hour === 24) return '12 AM';
    if (hour === 12) return '12 PM';
    return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
  };

  const getHourRange = (start, end) => {
    if (start === 0 && end === 24) {
      return 'All Day';
    }
    return `${getHourLabel(start)} - ${getHourLabel(end)}`;
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  return (
    <div className="admin-pricing">
      <div className="pricing-header">
        <h2>Pricing Rules</h2>
        <button 
          className="add-btn"
          onClick={() => { resetForm(); setShowForm(true); }}
        >
          + Add Pricing Rule
        </button>
      </div>

      {message && <div className="success">{message}</div>}
      {error && <div className="error">{error}</div>}

      {showForm && (
        <div className="pricing-form-container">
          <h3>{editingId ? 'Edit Pricing Rule' : 'Add New Pricing Rule'}</h3>
          <form onSubmit={handleSubmit} className="pricing-form">
            <div className="form-row">
              <label>
                Rule Name (optional)
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Winter Peak, Summer Off-Peak"
                />
              </label>
              <label>
                Priority
                <input
                  type="number"
                  value={form.priority}
                  onChange={e => setForm({ ...form, priority: e.target.value })}
                  placeholder="0"
                  min="0"
                  max="100"
                  title="Higher priority rules override lower priority rules. Default: 0 for recurring, 10 for specific dates."
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                Start Date *
                <input
                  type="date"
                  value={form.start_date}
                  onChange={e => setForm({ ...form, start_date: e.target.value })}
                  required
                />
              </label>
              <label>
                End Date *
                <input
                  type="date"
                  value={form.end_date}
                  min={form.is_recurring ? undefined : form.start_date}
                  onChange={e => setForm({ ...form, end_date: e.target.value })}
                  required
                />
              </label>
              <label className="checkbox-label recurring-checkbox">
                <input
                  type="checkbox"
                  checked={form.is_recurring}
                  onChange={e => setForm({ ...form, is_recurring: e.target.checked })}
                />
                <span>Repeats every year</span>
              </label>
            </div>
            <div className="form-row">
              <label>
                Start Time *
                <select
                  value={form.start_hour}
                  onChange={e => setForm({ ...form, start_hour: e.target.value })}
                  required
                >
                  {HOURS.filter(h => h.value < 24).map(h => (
                    <option key={h.value} value={h.value}>{h.label}</option>
                  ))}
                </select>
              </label>
              <label>
                End Time *
                <select
                  value={form.end_hour}
                  onChange={e => setForm({ ...form, end_hour: e.target.value })}
                  required
                >
                  {HOURS.filter(h => h.value > parseInt(form.start_hour)).map(h => (
                    <option key={h.value} value={h.value}>{h.label}</option>
                  ))}
                </select>
              </label>
            </div>
            
            <div className="pricing-inputs-section">
              <h4>Prices per Hour</h4>
              <div className="form-row three-col">
                <label>
                  Full Turf
                  <div className="price-input-wrapper">
                    <span className="currency-symbol">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.full_turf_price}
                      onChange={e => setForm({ ...form, full_turf_price: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </label>
                <label>
                  Half Turf
                  <div className="price-input-wrapper">
                    <span className="currency-symbol">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.half_turf_price}
                      onChange={e => setForm({ ...form, half_turf_price: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </label>
                <label>
                  Cage Rental
                  <div className="price-input-wrapper">
                    <span className="currency-symbol">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.cage_price}
                      onChange={e => setForm({ ...form, cage_price: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </label>
              </div>
              <p className="form-hint">
                <em>Set prices for at least one booking type. Leave blank for types that don't apply.</em>
              </p>
              <p className="form-hint">
                <em><strong>Priority:</strong> Higher priority rules take precedence over lower ones for overlapping dates. 
                Use this to set special rates within a larger date range (e.g., holiday pricing within winter season).</em>
              </p>
            </div>
            
            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="save-btn">
                {editingId ? 'Update Rule' : 'Create Rule'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="pricing-rules-list">
        {rules.length === 0 ? (
          <p className="no-rules">No pricing rules set. Click "+ Add Pricing Rule" to create one.</p>
        ) : (
          <table className="pricing-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Date Range</th>
                <th>Hours</th>
                <th>Priority</th>
                <th>Full Turf</th>
                <th>Half Turf</th>
                <th>Cage</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map(rule => (
                <tr key={rule.id}>
                  <td>{rule.name || '-'}</td>
                  <td>{getDateRange(rule.start_date, rule.end_date, rule.is_recurring)}</td>
                  <td>{getHourRange(rule.start_hour || 6, rule.end_hour || 24)}</td>
                  <td className="priority-cell">{rule.priority ?? 0}</td>
                  <td className="price-cell">{formatPrice(rule.full_turf_price)}</td>
                  <td className="price-cell">{formatPrice(rule.half_turf_price)}</td>
                  <td className="price-cell">{formatPrice(rule.cage_price)}</td>
                  <td>
                    <button className="edit-btn" onClick={() => handleEdit(rule)}>
                      Edit
                    </button>
                    <button className="delete-btn" onClick={() => handleDelete(rule)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminPricing;
